import { useId } from 'react'

import classNames from 'classnames'
import { DataItem } from 'scrivito'

import { Avatar } from '@/Atomic/Molecules/Avatar'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { getDataItemInitials, getDataItemTitle } from '@/utils/dataClassMap'
import { getDataClassIcon } from '@/utils/image/getDataClassIcon'

import './AvatarGroup.scss'

const MAX_VISIBLE_AVATARS = 8

export interface AvatarGroupProps {
  dataItems: DataItem[]
  className?: string
}

const getTitle = (dataItem: DataItem) => getDataItemTitle(dataItem) ?? ''

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  dataItems,
  className,
}) => {
  const tooltipId = `avatar-group-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

  if (dataItems.length === 0) return null

  const visibleDataItems = dataItems.slice(0, MAX_VISIBLE_AVATARS)
  const hiddenDataItems = dataItems.slice(MAX_VISIBLE_AVATARS)
  const isSingle = dataItems.length === 1

  const renderAvatar = (dataItem: DataItem) => (
    <Avatar
      avatar={dataItem.get('image') as string | DataBinary | undefined}
      icon={getDataClassIcon(dataItem.dataClassName() ?? '')}
      initials={getDataItemInitials(dataItem)}
      initialsColorSeed={dataItem.id()}
      size="sm"
      className="m-avatar-group__avatar"
    />
  )

  if (isSingle) {
    const dataItem = dataItems[0]!
    const title = getTitle(dataItem)

    return (
      <div className={classNames('m-avatar-group', className)}>
        <div className="m-avatar-group__single">
          {renderAvatar(dataItem)}
          <span className="m-avatar-group__name">{title}</span>
        </div>
      </div>
    )
  }

  const hiddenNames = hiddenDataItems
    .slice(0, MAX_VISIBLE_AVATARS)
    .map(getTitle)
    .filter(Boolean)
    .join(', ')
  const tooltipRemainingCount = hiddenDataItems.length - MAX_VISIBLE_AVATARS
  const overflowTooltipContent =
    tooltipRemainingCount > 0
      ? `${hiddenNames}, +${tooltipRemainingCount}`
      : hiddenNames

  return (
    <div className={classNames('m-avatar-group', className)}>
      <div className="m-avatar-group__list">
        {visibleDataItems.map((dataItem, index) => {
          const title = getTitle(dataItem)

          return (
            <span
              key={dataItem.id()}
              className="m-avatar-group__item"
              data-tooltip-id={tooltipId}
              data-tooltip-content={title}
              aria-label={title}
              style={{ zIndex: visibleDataItems.length - index }}
            >
              {renderAvatar(dataItem)}
            </span>
          )
        })}
        {hiddenDataItems.length > 0 && (
          <span
            className="m-avatar-group__overflow"
            data-tooltip-id={tooltipId}
            data-tooltip-content={overflowTooltipContent}
            aria-label={overflowTooltipContent}
          >
            +{hiddenDataItems.length}
          </span>
        )}
      </div>
      <Tooltip id={tooltipId} size="xs" />
    </div>
  )
}
